import { TeachingBanner } from "../../components/TeachingBanner";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Search, UserPlus, Users, Mail, Building2, Sparkles, X, RefreshCw, Brain, MessageCircle, Phone, Calendar } from "lucide-react";
import {
  api,
  type ContactContextSummary,
  type FieldObservation,
  type PeopleFieldName,
  type PeopleSort,
  type Person,
  type PersonDetailResponse,
  type PersonFieldsResponse,
  type PersonInferredRelationship,
  type RelationshipType,
  type NodeNote,
} from "../../lib/api";
import { Markdown } from "../../components/Markdown";
import { WikiNote } from "../../components/WikiNote";
import { PersonSentimentBlock } from "../../components/SentimentTrend";
import { ViewFilter, useViewFilter } from "../../components/ViewFilter";
import { TeachingNote } from "../../components/TeachingNote";
import { InlineRelationshipConfirm } from "../../components/InlineRelationshipConfirm";
import { useRelationshipTaxonomy } from "../../lib/relationship-taxonomy";
import type { DateItem } from "../../lib/api";
import {
  relativeDayChip,
  formatNextOccurrence,
  originYearLabel,
  kindLabel as dateKindLabel,
} from "../../lib/dates-ui";
import { RelationshipRevisionPill } from "../../components/RelationshipRevisionPill";
import { MemsoPicksStrip } from "../../components/MemsoPicksStrip";

const SORT_OPTIONS: Array<{ id: PeopleSort; label: string; hint: string }> = [
  { id: "strength", label: "Strength", hint: "By interaction strength (active + recent first)" },
  { id: "recent", label: "Recent", hint: "Most-recently-touched first" },
  { id: "alpha", label: "A → Z", hint: "Alphabetical" },
  { id: "frequent", label: "Most active", hint: "By interaction count" },
];

const LEGACY_RELATIONSHIP_LABELS: Partial<Record<string, string>> = {
  colleague: "Colleague",
  family: "Family",
  friend: "Friend",
  vendor: "Vendor",
  client: "Client",
  manager: "Manager",
  report: "Report",
  other: "Other",
};

/** Humanize any taxonomy label or custom string. Falls back to the
 *  raw key when no special-case applies (the LLM might land
 *  "best_friend" or "mother_in_law" → we just title-case underscores). */
export function humanizeRelationship(
  type: string | null | undefined,
  custom?: string | null,
): string {
  if (custom && type === "custom") return custom;
  if (!type) return "—";
  if (LEGACY_RELATIONSHIP_LABELS[type]) return LEGACY_RELATIONSHIP_LABELS[type]!;
  if (type === "unknown") return "Unknown";
  if (type === "custom") return custom || "Custom";
  // mother_in_law -> Mother in Law
  return type
    .split("_")
    .map((s) => (s ? s[0].toUpperCase() + s.slice(1) : s))
    .join(" ");
}

/** Tint per relationship category. Loose categorization — family
 *  (rose), professional (sky), service (slate), social (violet),
 *  unknown (amber). */
function relationshipTint(type: string | null | undefined): string {
  if (!type) return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";
  const family = new Set([
    "wife", "husband", "partner", "ex_wife", "ex_husband", "ex_partner",
    "girlfriend", "boyfriend", "ex_girlfriend", "ex_boyfriend", "dating",
    "mother", "father", "parent", "stepmother", "stepfather",
    "daughter", "son", "child", "stepdaughter", "stepson",
    "sister", "brother", "sibling",
    "grandmother", "grandfather", "grandparent",
    "granddaughter", "grandson", "grandchild",
    "aunt", "uncle", "cousin", "niece", "nephew",
    "mother_in_law", "father_in_law", "sister_in_law", "brother_in_law",
    "family", "family_friend",
  ]);
  const professional = new Set([
    "manager", "skip_level_manager", "direct_report", "skip_level_report",
    "coworker", "peer", "mentor", "mentee", "colleague",
    "business_partner", "co_founder", "investor", "advisor",
    "customer", "client", "vendor", "supplier", "contractor",
    "recruiter", "candidate", "report",
  ]);
  const service = new Set([
    "doctor", "dentist", "therapist", "lawyer", "accountant", "financial_advisor",
    "hairdresser", "mechanic", "contractor_home", "realtor", "landlord",
    "teacher", "professor", "coach", "trainer", "religious_leader",
  ]);
  const social = new Set([
    "neighbor", "roommate", "best_friend", "close_friend", "friend",
    "acquaintance", "classmate", "student", "vendor",
  ]);
  if (family.has(type)) return "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200";
  if (professional.has(type)) return "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200";
  if (service.has(type)) return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";
  if (social.has(type)) return "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200";
  if (type === "unknown") return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200";
  return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";
}

export function People() {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState<PeopleSort>("strength");
  const [search, setSearch] = useState("");
  const [relFilter, setRelFilter] = useState<string>("");
  const taxo = useRelationshipTaxonomy();
  const [error, setError] = useState<string | null>(null);
  const [activePersonId, setActivePersonId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractToast, setExtractToast] = useState<string | null>(null);
  const [gmailConnected, setGmailConnected] = useState<boolean>(false);

  // Probe whether the user has Gmail connected so the empty state CTA
  // doesn't say "Connect Gmail" to a user who already has. Best-effort;
  // failure leaves the CTA showing (the safer default for new users).
  useEffect(() => {
    let cancelled = false;
    api
      .connectors()
      .then((d) => {
        if (cancelled) return;
        const has = d.connectors.some(
          (c) => c.kind === "gmail" && c.enabled,
        );
        setGmailConnected(has);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const { state: filter, setState: setFilter, loaded: filterLoaded } = useViewFilter("people");

  const refresh = useCallback(() => {
    api
      .peopleList({
        sort,
        search: search || undefined,
        relationship: relFilter || undefined,
        limit: 100,
        include_test: filter.includeTest,
      })
      .then((d) => {
        setPeople(d.people);
        setTotal(d.total);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, [sort, search, relFilter, filter.includeTest]);

  useEffect(() => {
    if (!filterLoaded) return;
    refresh();
  }, [refresh, filterLoaded]);

  const onExtract = useCallback(async () => {
    setExtracting(true);
    setExtractToast(null);
    try {
      const r = await api.peopleExtract();
      const newCount = r.peopleNew;
      const updated = r.peopleUpdated;
      const interactions = r.interactionsNew;
      setExtractToast(
        newCount + updated + interactions === 0
          ? "No new people surfaced. Connect Gmail or Calendar to weave more in."
          : `Wove in ${newCount} new · refreshed ${updated} · ${interactions} interactions logged.`,
      );
      refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setExtractToast(`Extraction failed: ${msg}`);
    } finally {
      setExtracting(false);
      window.setTimeout(() => setExtractToast(null), 6000);
    }
  }, [refresh]);

  /** Per-packet enrichment: read each email/Teams/WhatsApp packet
   *  and extract employer / role / team / tone / commitments via
   *  Gemini Flash. Then aggregate per-contact evidence into people
   *  rows. */
  const onEnrich = useCallback(async () => {
    setExtracting(true);
    setExtractToast("Reading every packet to extract contact context...");
    try {
      const r = await api.peopleEnrich({ max_packets: 50 });
      const e = r.enrichment;
      const a = r.aggregation;
      setExtractToast(
        `Enriched ${e.enriched} packets (skipped ${e.skipped_no_text + e.skipped_unsupported_type}, ${e.errors} errors). Updated ${a.contactsUpdated} contacts with ${a.relationshipsInserted + a.relationshipsUpdated} new/refreshed relationships and stamped ${a.domainColleagueStamped} as colleagues by domain.`,
      );
      refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setExtractToast(`Enrichment failed: ${msg}`);
    } finally {
      setExtracting(false);
      window.setTimeout(() => setExtractToast(null), 12_000);
    }
  }, [refresh]);

  /** Cross-source aggregation + LLM relationship inference. Folds
   *  duplicate contacts across Gmail / Calendar / Outlook / Teams /
   *  WhatsApp and runs aggressive relationship inference (wife / dad /
   *  manager / dentist / etc.) via the Sonnet binding. */
  const onAggregate = useCallback(async () => {
    setExtracting(true);
    setExtractToast("Weaving contacts across all sources & inferring relationships…");
    try {
      const r = await api.peopleAggregate({
        infer_relationships: true,
        max_contacts: 50,
      });
      const a = r.aggregation;
      const i = r.inference;
      setExtractToast(
        i
          ? `Aggregated ${a.packetsScanned} packets · ${a.contactsNew} new · ${a.identitiesMerged} merged · ${i.inferred + i.updated} relationships inferred (${i.skipped_low_confidence} too uncertain).`
          : `Aggregated ${a.packetsScanned} packets · ${a.contactsNew} new · ${a.identitiesMerged} merged.`,
      );
      refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setExtractToast(`Aggregation failed: ${msg}`);
    } finally {
      setExtracting(false);
      window.setTimeout(() => setExtractToast(null), 10_000);
    }
  }, [refresh]);

  const isEmpty = people !== null && people.length === 0 && search === "";

  return (
    <section className="space-y-4">
      <TeachingBanner id="people_overview" />
      <TeachingBanner id="scope_filter" />
      <TeachingNote
        id="people.v1"
        tone="violet"
        title="People is everyone Memso has met across your captures."
        body="Names, roles, and relationships are inferred from emails, calendar invites, and notes. Amber chips flag inferences awaiting your confirmation; tap a person to see what was found and lock it in."
      />
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="relative flex-1 min-w-0 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="search"
              name="memso-people-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              aria-label="Search people"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/40 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>
          <div className="hidden md:flex items-center gap-1 bg-slate-100 dark:bg-slate-800/40 rounded-xl p-1 border border-slate-200 dark:border-slate-700">
            {SORT_OPTIONS.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setSort(o.id)}
                aria-pressed={sort === o.id}
                title={o.hint}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  sort === o.id
                    ? "bg-violet-600 text-white shadow-sm"
                    : "text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700/40"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <ViewFilter state={filter} onChange={setFilter} />
          <button
            type="button"
            onClick={onExtract}
            disabled={extracting}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-50 transition-colors"
            aria-label="Re-scan captures for new people"
            title="Quick rescan: walks Gmail/Calendar packets and folds new contacts in"
          >
            {extracting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Re-scan</span>
          </button>
          <button
            type="button"
            onClick={onAggregate}
            disabled={extracting}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border border-violet-300 dark:border-violet-700 bg-gradient-to-br from-violet-50 to-fuchsia-50 dark:from-violet-950/40 dark:to-fuchsia-950/40 text-violet-700 dark:text-violet-200 hover:from-violet-100 hover:to-fuchsia-100 disabled:opacity-50 transition-colors"
            aria-label="Cross-source aggregate + infer relationships"
            title="Walks every packet across Gmail, Calendar, Outlook, Teams, WhatsApp; merges duplicates by email/phone/JID; runs LLM relationship inference (wife / father / manager / dentist / ...)"
          >
            <Brain className="w-4 h-4" />
            <span className="hidden sm:inline">Aggregate + infer</span>
          </button>
          <button
            type="button"
            onClick={onEnrich}
            disabled={extracting}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border border-emerald-300 dark:border-emerald-700 bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-950/40 dark:to-cyan-950/40 text-emerald-700 dark:text-emerald-200 hover:from-emerald-100 hover:to-cyan-100 disabled:opacity-50 transition-colors"
            aria-label="Per-packet AI enrichment"
            title="Reads every email / Teams / WhatsApp packet body and extracts contact context: employer, role, team, tone, deadlines, commitments. Default Gemini Flash; cost is roughly $0.0001 per packet."
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Enrich packets</span>
          </button>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-violet-600 text-white shadow-sm hover:bg-violet-700 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Add person</span>
          </button>
        </div>
      </div>

      {taxo.roots.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={() => setRelFilter("")}
            aria-pressed={relFilter === ""}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
              relFilter === ""
                ? "bg-violet-600 text-white"
                : "bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            Everyone
          </button>
          {taxo.roots.map((root) => (
            <button
              key={root.type}
              type="button"
              onClick={() =>
                setRelFilter(relFilter === root.type ? "" : root.type)
              }
              aria-pressed={relFilter === root.type}
              title={`Show only ${root.display_name} (includes every sub-type)`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                relFilter === root.type
                  ? "bg-violet-600 text-white"
                  : "bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              {root.display_name}
            </button>
          ))}
        </div>
      )}

      <div className="flex md:hidden items-center gap-1 bg-slate-100 dark:bg-slate-800/40 rounded-xl p-1 border border-slate-200 dark:border-slate-700 self-start w-full overflow-x-auto">
        {SORT_OPTIONS.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => setSort(o.id)}
            aria-pressed={sort === o.id}
            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              sort === o.id
                ? "bg-violet-600 text-white shadow-sm"
                : "text-slate-700 dark:text-slate-200"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      {extractToast && (
        <div className="rounded-xl px-3 py-2 text-xs font-medium text-violet-900 dark:text-violet-100 bg-violet-100/70 dark:bg-violet-900/30 border border-violet-300/60 dark:border-violet-700/40">
          {extractToast}
        </div>
      )}

      {error && people !== null && (
        <div className="text-sm text-rose-600 dark:text-rose-300">{error}</div>
      )}

      {/* W070.7: additive curated strip above the people directory.
          Collapses silently when the curator is empty / down; the full
          directory grid below is always the fallback. */}
      <MemsoPicksStrip<Person>
        surface="people"
        title="Memso picks"
        subtitle="The people most worth reconnecting with now, picked by Memso."
        load={() =>
          api.peopleCurate().then((r) => ({ picks: r.picks, items: r.people }))
        }
        itemKindOf={() => "person"}
        onOpen={() => {
          /* the card's own onClick opens the dossier; this hook only
             exists so the strip emits an open_source attention event
             under the people surface key. */
        }}
      >
        {(p) => (
          <PersonPickCard person={p} onOpen={() => setActivePersonId(p.id)} />
        )}
      </MemsoPicksStrip>

      {people === null && error ? (
        <section className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-8 sm:p-12 text-center">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-400 to-fuchsia-500 items-center justify-center text-white shadow-md mb-4">
            <Users className="w-7 h-7" strokeWidth={2.5} />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">People</h2>
          <p className="text-base sm:text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
            People is everyone Memso has met across your captures, emails, and
            meetings. We could not load the directory just now.
          </p>
          <button
            type="button"
            onClick={() => refresh()}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-violet-600 text-white shadow-sm hover:bg-violet-700 transition-colors mt-5"
          >
            <Sparkles className="w-4 h-4" />
            Try again
          </button>
        </section>
      ) : people === null ? (
        <SkeletonGrid />
      ) : isEmpty ? (
        <EmptyState onExtract={onExtract} extracting={extracting} gmailConnected={gmailConnected} />
      ) : people.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 italic px-1">
          No people match “{search}”.
        </p>
      ) : (
        <PeopleGrid
          people={people}
          total={total}
          activeId={activePersonId}
          onOpen={(id) => setActivePersonId(id)}
          onChanged={refresh}
        />
      )}

      {activePersonId && (
        <PersonDetail
          id={activePersonId}
          onClose={() => setActivePersonId(null)}
          onChanged={refresh}
        />
      )}

      {showAdd && (
        <AddPersonDialog
          onClose={() => setShowAdd(false)}
          onCreated={(id) => {
            setShowAdd(false);
            refresh();
            setActivePersonId(id);
          }}
        />
      )}
    </section>
  );
}

function SkeletonGrid() {
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <li
          key={i}
          className="h-20 rounded-2xl bg-slate-100 dark:bg-slate-800/40 animate-pulse"
        />
      ))}
    </ul>
  );
}

function EmptyState({
  onExtract,
  extracting,
  gmailConnected,
}: {
  onExtract: () => void;
  extracting: boolean;
  gmailConnected: boolean;
}) {
  return (
    <section className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-8 sm:p-12 text-center">
      <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-400 to-fuchsia-500 items-center justify-center text-white shadow-md mb-4">
        <Users className="w-7 h-7" strokeWidth={2.5} />
      </div>
      <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">People</h2>
      <p className="text-base sm:text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
        {gmailConnected
          ? "Memso will weave in people as your Gmail and Calendar sync. Re-scan now to backfill from existing captures."
          : "Memso will weave in people as they show up across your captures, emails, and meetings."}
      </p>
      <div className="flex items-center justify-center gap-2 mt-5">
        <button
          type="button"
          onClick={onExtract}
          disabled={extracting}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-violet-600 text-white shadow-sm hover:bg-violet-700 disabled:opacity-50 transition-colors"
        >
          <Sparkles className="w-4 h-4" />
          {extracting ? "Scanning…" : "Re-scan captures"}
        </button>
        {!gmailConnected && (
          <a
            href="/settings?section=connectors"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-800/40 transition-colors"
          >
            Connect Gmail
          </a>
        )}
      </div>
    </section>
  );
}

/** W070.7: person card for the curated "Memso picks" strip. Mirrors a
 *  single PeopleGrid cell so the strip matches the directory styling
 *  without refactoring the grid. */
function PersonPickCard({
  person,
  onOpen,
}: {
  person: Person;
  onOpen: () => void;
}) {
  const role = person.role || person.inferred_role || null;
  const org = person.organization || person.inferred_employer || null;
  const subtitle = [role, org].filter(Boolean).join(" · ");
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left rounded-2xl p-3 border transition-all border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 hover:border-violet-300 dark:hover:border-violet-700 hover:shadow-sm"
    >
      <div className="flex items-center gap-3">
        <Avatar person={person} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">
              {person.display_name}
            </div>
            {person.relationship && (
              <RelationshipChip relationship={person.relationship} />
            )}
          </div>
          {subtitle && (
            <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {subtitle}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function PeopleGrid({
  people,
  total,
  activeId,
  onOpen,
  onChanged,
}: {
  people: Person[];
  total: number;
  activeId: string | null;
  onOpen: (id: string) => void;
  onChanged?: () => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500 dark:text-slate-400 px-1">
        {people.length} of {total} {total === 1 ? "person" : "people"}
      </p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2">
        {people.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onOpen(p.id)}
              aria-pressed={activeId === p.id}
              className={`w-full text-left rounded-2xl p-3 border transition-all ${
                activeId === p.id
                  ? "border-violet-400 dark:border-violet-500 bg-violet-50 dark:bg-violet-950/30 shadow-md"
                  : "border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 hover:border-violet-300 dark:hover:border-violet-700 hover:shadow-sm"
              }`}
            >
              <div className="flex items-center gap-3">
                <Avatar person={p} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <div className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">
                      {p.display_name}
                    </div>
                    {p.relationship && (
                      <RelationshipChip relationship={p.relationship} />
                    )}
                  </div>
                  {(() => {
                    const role = p.role || p.inferred_role || null;
                    const org = p.organization || p.inferred_employer || null;
                    const team = p.inferred_team || null;
                    const subtitle = [role, org].filter(Boolean).join(" · ");
                    if (subtitle) {
                      return (
                        <div className="text-xs text-slate-500 dark:text-slate-400 truncate flex items-center gap-1">
                          <span className="truncate">{subtitle}</span>
                          {team && (
                            <span className="px-1 py-0.5 rounded bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200 text-[10px] font-semibold flex-shrink-0">
                              {team}
                            </span>
                          )}
                        </div>
                      );
                    }
                    if (p.primary_email) {
                      return (
                        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {p.primary_email}
                        </div>
                      );
                    }
                    return (
                      <div className="text-xs text-slate-400 italic">No details yet</div>
                    );
                  })()}
                  {p.sources_seen && p.sources_seen.length > 0 && (
                    <SourcesRow sources={p.sources_seen} tone={p.tone_to_user ?? null} strength={p.relationship_strength_score ?? null} packets={p.evidence_packet_count ?? 0} />
                  )}
                </div>
                <div className="flex flex-col items-end gap-0.5 text-right text-[10px] text-slate-400 dark:text-slate-500 flex-shrink-0">
                  <span>{relativeTime(p.last_interacted_at)}</span>
                  {(p.interactionCount || 0) > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700/50 font-semibold">
                      {p.interactionCount}
                    </span>
                  )}
                </div>
              </div>
            </button>
            {p.relationship &&
              (!p.relationship.confirmed ||
                p.relationship.inference_source === "auto_confirmed") && (
                <div className="px-3 pb-2 pt-1">
                  <InlineRelationshipConfirm
                    relationship={p.relationship}
                    personName={p.display_name}
                    onChanged={onChanged}
                  />
                </div>
              )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Compact chip rendering the inferred relationship label. Inferred
 *  rows render dashed; confirmed rows render solid. Tooltip exposes
 *  the LLM reasoning so the user can audit before tapping into the
 *  detail panel. */
function RelationshipChip({ relationship }: { relationship: PersonInferredRelationship }) {
  const taxo = useRelationshipTaxonomy();
  const inferred = !relationship.confirmed;
  // The most-specific thing we have: a tentative subtype guess wins
  // for display (rendered tentative); else the committed type.
  const hasSubtypeGuess =
    !!relationship.inferred_subtype &&
    relationship.inferred_subtype !== relationship.type;
  const specific = hasSubtypeGuess
    ? relationship.inferred_subtype!
    : relationship.type;
  const tint = relationshipTint(specific);
  const label =
    relationship.type === "custom" && relationship.label_custom
      ? relationship.label_custom
      : taxo.humanize(specific, relationship.label_custom);
  const rootEntry = taxo.rootOf(specific);
  const showParent = rootEntry && rootEntry.type !== specific;
  const tooltipParts: string[] = [];
  const chain = taxo.chain(specific);
  if (chain.length > 1) {
    tooltipParts.push(chain.map((c) => taxo.humanize(c)).join(" › "));
  }
  if (hasSubtypeGuess) {
    tooltipParts.push(
      `Tentative guess (${taxo.humanize(relationship.inferred_subtype)}); confirmed as ${taxo.humanize(relationship.type)}.`,
    );
  } else if (inferred) {
    tooltipParts.push("Inferred — tap to confirm.");
  }
  if (relationship.inference_confidence != null) {
    tooltipParts.push(`Confidence: ${(relationship.inference_confidence * 100).toFixed(0)}%`);
  }
  if (relationship.inference_reasoning) {
    tooltipParts.push(relationship.inference_reasoning);
  }
  if (relationship.inference_evidence?.length) {
    tooltipParts.push(`Evidence: "${relationship.inference_evidence[0]}"`);
  }
  const tentative = inferred || hasSubtypeGuess;
  return (
    <span
      className="inline-flex items-center gap-1 flex-shrink-0"
      title={tooltipParts.join(" · ")}
    >
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${tint} ${
          tentative ? "border border-dashed border-amber-400/70 dark:border-amber-500/70" : ""
        }`}
      >
        {label}
      </span>
      {showParent && (
        <span className="text-[9px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wide">
          {rootEntry!.short_name}
        </span>
      )}
    </span>
  );
}

/** Tiny icon row showing which surfaces have evidence for this
 *  contact. Email / WhatsApp / Teams / Calendar / Phone. Adds a tone
 *  pill, strength bar, and packet count once enrichment lands. */
function SourcesRow({
  sources,
  tone,
  strength,
  packets,
}: {
  sources: string[];
  tone?: string | null;
  strength?: number | null;
  packets?: number;
}) {
  const set = new Set(sources);
  const icons: Array<{ key: string; Icon: typeof Mail; title: string }> = [];
  if (set.has("gmail") || set.has("outlook")) icons.push({ key: "email", Icon: Mail, title: "Email" });
  if (set.has("calendar")) icons.push({ key: "calendar", Icon: Calendar, title: "Calendar" });
  if (set.has("whatsapp")) icons.push({ key: "whatsapp", Icon: MessageCircle, title: "WhatsApp" });
  if (set.has("teams")) icons.push({ key: "teams", Icon: Users, title: "Teams" });
  if (icons.length === 0 && !tone && !strength && !packets) return null;
  return (
    <div className="flex items-center gap-1.5 mt-1 text-slate-400 dark:text-slate-500 flex-wrap">
      {icons.map((i) => (
        <i.Icon key={i.key} className="w-3 h-3" aria-label={i.title} />
      ))}
      {tone && (
        <span
          className={`px-1 py-0.5 rounded text-[9px] font-semibold ${toneTint(tone)}`}
          title={`Tone toward you: ${tone}`}
        >
          {tone}
        </span>
      )}
      {(packets ?? 0) > 0 && (
        <span
          className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400 text-[9px] font-semibold"
          title={`${packets} enriched packet(s) feeding this contact's profile`}
        >
          {packets} pkt
        </span>
      )}
      {strength != null && strength > 0 && (
        <span
          className="px-1 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-200 text-[9px] font-semibold"
          title={`Relationship strength score (volume + recency + tone): ${(strength * 100).toFixed(0)}%`}
        >
          {(strength * 100).toFixed(0)}%
        </span>
      )}
    </div>
  );
}

function toneTint(tone: string): string {
  switch (tone) {
    case "friendly": return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200";
    case "casual": return "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200";
    case "formal": return "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200";
    case "hostile": return "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200";
    default: return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";
  }
}

function Avatar({ person }: {
  person: { display_name: string; avatar_url: string | null; is_fully_confirmed?: boolean };
}) {
  // Tier-1 inferred state: dashed amber border around the avatar so
  // the contact reads as "Memso pulled this together; tap to confirm"
  // at a glance from the directory grid. Confirmed contacts render
  // solid. Wraps the avatar in a slightly-larger circle so the dashes
  // sit OUTSIDE the avatar art rather than clipping the photo.
  const inferred = person.is_fully_confirmed === false;
  const inner = person.avatar_url ? (
    <img
      src={person.avatar_url}
      alt=""
      className="w-10 h-10 rounded-full object-cover bg-slate-200 dark:bg-slate-700"
    />
  ) : (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-sm"
      style={{
        background: `linear-gradient(135deg, hsl(${hashHue(person.display_name)}, 70%, 55%), hsl(${(hashHue(person.display_name) + 40) % 360}, 70%, 50%))`,
      }}
      aria-hidden="true"
    >
      {initialsFor(person.display_name)}
    </div>
  );
  if (!inferred) return <div className="flex-shrink-0">{inner}</div>;
  return (
    <div
      className="rounded-full p-0.5 border-2 border-dashed border-amber-400 dark:border-amber-500 flex-shrink-0"
      title="Inferred contact — open to confirm"
    >
      {inner}
    </div>
  );
}

function PersonDetail({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [data, setData] = useState<PersonDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [showAddRel, setShowAddRel] = useState(false);

  const refresh = useCallback(() => {
    setData(null);
    api
      .personGet(id)
      .then((d) => setData(d))
      .catch((e: Error) => setError(e.message));
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 bg-slate-950/40 dark:bg-slate-950/70 backdrop-blur-sm flex justify-end sm:items-stretch animate-in fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full sm:max-w-xl bg-white dark:bg-slate-900 sm:border-l border-slate-200 dark:border-slate-700 shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 backdrop-blur">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Person</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {error && (
          <div className="px-4 sm:px-6 py-3 text-sm text-rose-600 dark:text-rose-300">{error}</div>
        )}

        {!data ? (
          <div className="px-4 sm:px-6 py-6 space-y-3">
            <div className="h-16 rounded-2xl bg-slate-100 dark:bg-slate-800/40 animate-pulse" />
            <div className="h-32 rounded-2xl bg-slate-100 dark:bg-slate-800/40 animate-pulse" />
          </div>
        ) : editing ? (
          <EditPersonForm
            person={data.person}
            onCancel={() => setEditing(false)}
            onSaved={() => {
              setEditing(false);
              refresh();
              onChanged();
            }}
          />
        ) : (
          <div className="px-4 sm:px-6 py-5 space-y-6">
            <div className="flex items-start gap-4">
              <Avatar person={data.person} />
              <div className="min-w-0 flex-1">
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                  {data.person.display_name}
                </h3>
                {(data.person.role || data.person.organization) && (
                  <div className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">
                    {[data.person.role, data.person.organization].filter(Boolean).join(" · ")}
                  </div>
                )}
                {data.person.primary_email && (
                  <a
                    href={`mailto:${data.person.primary_email}`}
                    className="inline-flex items-center gap-1.5 text-sm text-violet-700 dark:text-violet-300 mt-1 hover:underline"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    {data.person.primary_email}
                  </a>
                )}
                {data.person.alt_emails.length > 0 && (
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Also: {data.person.alt_emails.join(", ")}
                  </div>
                )}
              </div>
            </div>

            {data.person.notes && (
              <div className="rounded-xl px-3 py-2.5 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
                {data.person.notes}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Edit
              </button>
              {data.person.primary_email && (
                <a
                  href={`mailto:${data.person.primary_email}`}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700"
                >
                  Compose email
                </a>
              )}
              <button
                type="button"
                onClick={() => setShowAddRel(true)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Add relationship
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!confirm(`Remove ${data.person.display_name} from your directory?`)) return;
                  await api.personDelete(data.person.id).catch(() => undefined);
                  onChanged();
                  onClose();
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30 ml-auto"
              >
                Remove
              </button>
            </div>

            <RelationshipRevisionPill
              personId={data.person.id}
              personName={data.person.display_name}
              onChanged={() => {
                refresh();
                onChanged();
              }}
            />

            <RelationshipBanner
              person={data.person}
              relationships={data.relationships}
              onChanged={() => {
                refresh();
                onChanged();
              }}
            />

            <KnowledgeBaseBlock
              person={data.person}
              context={data.contact_context ?? null}
              onEnrich={async () => {
                try {
                  await api.peopleEnrich({ packet_ids: [], force: false, max_packets: 50 });
                  refresh();
                } catch (e) { /* silent */ }
              }}
            />

            <NodeNoteBlock personId={data.person.id} />

            <FieldSourcesBlock personId={data.person.id} onChanged={refresh} />

            <RelationshipsBlock
              relationships={data.relationships}
              onRemove={async (relId) => {
                await api.relationshipDelete(relId).catch(() => undefined);
                refresh();
                onChanged();
              }}
            />

            <ImportantDatesBlock personId={data.person.id} />

            <PersonSentimentBlock personId={data.person.id} />

            <InteractionsBlock interactions={data.interactions} />

            <WeaveInsightsBlock insights={data.weave_insights ?? []} />

            <NoteMentionsBlock mentions={data.noteMentions} />
          </div>
        )}

        {showAddRel && data && (
          <AddRelationshipDialog
            personId={data.person.id}
            personName={data.person.display_name}
            onClose={() => setShowAddRel(false)}
            onCreated={() => {
              setShowAddRel(false);
              refresh();
              onChanged();
            }}
          />
        )}
      </div>
    </div>
  );
}

/** Top-of-detail panel: render the inferred or confirmed primary
 *  relationship prominently with reasoning, evidence quotes, and one-
 *  tap Confirm / Edit / Reject affordances per the inferred-vs-
 *  confirmed pattern. Only renders when there's at least one rel row. */
function RelationshipBanner({
  person,
  relationships,
  onChanged,
}: {
  person: PersonDetailResponse["person"];
  relationships: PersonDetailResponse["relationships"];
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const taxo = useRelationshipTaxonomy();
  const [editValue, setEditValue] = useState<string>("");

  const top = useMemo(() => {
    const visible = relationships.filter((r) => !(r as { rejected_at?: string | null }).rejected_at);
    if (visible.length === 0) return null;
    const confirmed = visible.find((r) => (r as { confirmed?: boolean }).confirmed === true);
    if (confirmed) return confirmed;
    return visible
      .slice()
      .sort(
        (a, b) =>
          ((b as { inference_confidence?: number | null }).inference_confidence ?? 0) -
          ((a as { inference_confidence?: number | null }).inference_confidence ?? 0),
      )[0];
  }, [relationships]);


  if (!top) {
    // No inferred relationship yet — surface a CTA to run inference.
    return (
      <section className="rounded-2xl border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-950/20 px-3 py-2.5 text-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <Brain className="w-4 h-4 text-amber-600 dark:text-amber-300" />
          <span className="text-amber-900 dark:text-amber-100 font-semibold">
            No relationship inferred yet.
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.relationshipsInferLlm({ person_ids: [person.id], force: true });
                onChanged();
              } finally {
                setBusy(false);
              }
            }}
            className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200 bg-white/60 dark:bg-slate-900/40 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50"
          >
            {busy ? "Asking model…" : "Infer with AI"}
          </button>
        </div>
      </section>
    );
  }

  const t = top as PersonDetailResponse["relationships"][number] & {
    confirmed?: boolean;
    inference_confidence?: number | null;
    inference_reasoning?: string | null;
    inference_evidence_json?: string | null;
    inferred_at?: string | null;
    inference_model?: string | null;
    relationship_label_custom?: string | null;
    inferred_subtype?: string | null;
  };
  const isConfirmed = t.confirmed === true;
  const subtypeGuess =
    t.inferred_subtype && t.inferred_subtype !== t.type
      ? t.inferred_subtype
      : null;
  const specific = subtypeGuess ?? t.type;
  const tint = relationshipTint(specific);
  const label =
    t.type === "custom" && t.relationship_label_custom
      ? t.relationship_label_custom
      : taxo.humanize(specific, t.relationship_label_custom);
  const rootEntry = taxo.rootOf(specific);
  const showParent = rootEntry && rootEntry.type !== specific;
  let evidence: string[] = [];
  if (t.inference_evidence_json) {
    try {
      const parsed = JSON.parse(t.inference_evidence_json);
      if (Array.isArray(parsed)) evidence = parsed.filter((s): s is string => typeof s === "string");
    } catch { /* ignore */ }
  }

  return (
    <section
      className={`rounded-2xl px-4 py-3 border ${
        isConfirmed
          ? "border-violet-300 dark:border-violet-700 bg-violet-50/60 dark:bg-violet-950/30"
          : "border-dashed border-amber-400/70 dark:border-amber-500/70 bg-amber-50/40 dark:bg-amber-950/10"
      }`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold ${tint}`}>
          {label}
        </span>
        {showParent && (
          <span
            className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide"
            title={taxo.chain(specific).map((c) => taxo.humanize(c)).join(" › ")}
          >
            {rootEntry!.display_name}
          </span>
        )}
        <span className="text-sm text-slate-500 dark:text-slate-400">·</span>
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
          {person.display_name}
        </span>
        {!isConfirmed && t.inference_confidence != null && (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">
            inferred · {(t.inference_confidence * 100).toFixed(0)}% confidence
          </span>
        )}
        {isConfirmed && (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-300">
            confirmed
          </span>
        )}
      </div>
      {t.inference_reasoning && !isConfirmed && (
        <p className="text-xs text-slate-700 dark:text-slate-200 mt-2 leading-relaxed">
          {t.inference_reasoning}
        </p>
      )}
      {evidence.length > 0 && !isConfirmed && (
        <ul className="text-[11px] text-slate-600 dark:text-slate-400 mt-1.5 space-y-0.5 list-disc pl-4">
          {evidence.slice(0, 3).map((q, i) => (
            <li key={i} className="italic">"{q}"</li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {!isConfirmed && (
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.relationshipConfirm(t.id);
                onChanged();
              } finally {
                setBusy(false);
              }
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
          >
            <Check className="w-3.5 h-3.5" />
            Confirm
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setEditValue(subtypeGuess ?? t.type);
            setEditing((v) => !v);
          }}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-800/40"
        >
          {editing ? "Cancel" : "Edit"}
        </button>
        {!isConfirmed && (
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.relationshipReject(t.id);
                onChanged();
              } finally {
                setBusy(false);
              }
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30"
          >
            Reject
          </button>
        )}
        {!isConfirmed && (
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.relationshipsInferLlm({ person_ids: [person.id], force: true });
                onChanged();
              } finally {
                setBusy(false);
              }
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-950/30 disabled:opacity-50 ml-auto"
          >
            <Brain className="w-3.5 h-3.5" />
            Re-infer
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-2 pt-2 border-t border-amber-200/60 dark:border-amber-800/40">
          <p className="text-xs text-slate-600 dark:text-slate-300 mb-1.5">
            Pick the right relationship
          </p>
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/40 text-xs text-slate-900 dark:text-slate-100"
          >
            {taxo.roots.length === 0 ? (
              <option value={t.type}>{taxo.humanize(t.type)}</option>
            ) : (
              taxo.roots.map((root) => (
                <optgroup key={root.type} label={root.display_name}>
                  {taxo.userFacingDescendants(root.type).map((opt) => (
                    <option key={opt.type} value={opt.type}>
                      {opt.display_name}
                    </option>
                  ))}
                </optgroup>
              ))
            )}
          </select>
          <div className="flex gap-1.5 mt-2">
            <button
              type="button"
              disabled={busy || !editValue}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.relationshipConfirm(t.id, { type: editValue as RelationshipType });
                  onChanged();
                  setEditing(false);
                } finally {
                  setBusy(false);
                }
              }}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Save & confirm
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/** Accumulated knowledge base for one contact: employer / role /
 *  team / topics / hierarchical signals / per-packet evidence
 *  trail. Built from the contact-context-enricher's per-packet
 *  output via the evidence aggregator. */
function KnowledgeBaseBlock({
  person,
  context,
  onEnrich,
}: {
  person: Person;
  context: ContactContextSummary | null | undefined;
  onEnrich: () => void;
}) {
  const hasAny =
    person.inferred_employer ||
    person.inferred_role ||
    person.inferred_team ||
    person.tone_to_user ||
    (person.topics_associated?.length ?? 0) > 0 ||
    (person.evidence_packet_count ?? 0) > 0 ||
    (context?.evidence_packets?.length ?? 0) > 0;

  if (!hasAny) {
    return (
      <section className="rounded-2xl border border-dashed border-emerald-300 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-950/10 px-3 py-2.5">
        <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-1 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" />
          Knowledge base
        </h4>
        <p className="text-xs text-slate-600 dark:text-slate-300">
          No per-packet enrichment yet for this contact. Tap "Enrich" to read every email/Teams/WhatsApp message and pull employer, role, team, tone, and commitments.
        </p>
        <button
          type="button"
          onClick={onEnrich}
          className="mt-2 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
        >
          Run enrichment now
        </button>
      </section>
    );
  }

  const supervisorCount = context?.hierarchical_signals?.likely_supervisor_count ?? 0;
  const subordinateCount = context?.hierarchical_signals?.likely_subordinate_count ?? 0;
  const peerCount = context?.hierarchical_signals?.likely_peer_count ?? 0;

  return (
    <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 px-3 py-3 space-y-2.5">
      <header className="flex items-center justify-between">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" />
          Knowledge base
        </h4>
        <span className="text-[10px] text-slate-400 dark:text-slate-500">
          {person.evidence_packet_count ?? 0} packet{(person.evidence_packet_count ?? 0) === 1 ? "" : "s"} indexed
        </span>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
        {person.inferred_employer && (
          <Fact label="Employer" value={person.inferred_employer} icon={Building2} />
        )}
        {person.inferred_role && (
          <Fact label="Role" value={person.inferred_role} />
        )}
        {person.inferred_team && (
          <Fact label="Team" value={person.inferred_team} />
        )}
        {person.tone_to_user && (
          <Fact label="Tone" value={person.tone_to_user} />
        )}
        {person.relationship_strength_score != null && (
          <Fact
            label="Strength"
            value={`${(person.relationship_strength_score * 100).toFixed(0)}%`}
          />
        )}
        {(supervisorCount + subordinateCount + peerCount) > 0 && (
          <Fact
            label="Hierarchy"
            value={
              [
                supervisorCount > 0 && `${supervisorCount}× supervisor signal`,
                subordinateCount > 0 && `${subordinateCount}× subordinate`,
                peerCount > 0 && `${peerCount}× peer`,
              ].filter(Boolean).join(", ") || "none"
            }
          />
        )}
      </div>
      {(person.topics_associated?.length ?? 0) > 0 && (
        <div>
          <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1">
            Topics associated
          </h5>
          <ul className="flex flex-wrap gap-1">
            {(person.topics_associated ?? []).slice(0, 12).map((t) => (
              <li
                key={t}
                className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700/40 text-slate-700 dark:text-slate-200 text-[10px] font-semibold"
              >
                {t}
              </li>
            ))}
          </ul>
        </div>
      )}
      {(context?.evidence_packets?.length ?? 0) > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-500 dark:text-slate-400 font-semibold">
            Evidence trail ({context!.evidence_packets.length} packets)
          </summary>
          <ul className="mt-1.5 space-y-1.5 max-h-64 overflow-y-auto">
            {context!.evidence_packets.slice(0, 12).map((ep) => (
              <li
                key={ep.packet_id}
                className="rounded-lg px-2 py-1.5 bg-slate-50 dark:bg-slate-800/30 border border-slate-200/60 dark:border-slate-700/40"
              >
                <div className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400">
                  <span>{relativeTime(ep.captured_at)}</span>
                  <span>·</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-200">
                    {humanizeRelationship(ep.relationship, null)}
                  </span>
                  <span className="text-slate-400">{(ep.confidence * 100).toFixed(0)}%</span>
                </div>
                {ep.quote && (
                  <p className="text-[11px] text-slate-700 dark:text-slate-200 italic mt-0.5">
                    "{ep.quote}"
                  </p>
                )}
                {(ep.employer || ep.role || ep.team) && (
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                    {[ep.employer, ep.role, ep.team].filter(Boolean).join(" · ")}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
      <button
        type="button"
        onClick={onEnrich}
        className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 hover:underline"
      >
        Re-enrich this contact
      </button>
    </section>
  );
}

function Fact({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon?: typeof Mail;
}) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-50 dark:bg-slate-800/30 border border-slate-200/60 dark:border-slate-700/40">
      {Icon && <Icon className="w-3 h-3 text-slate-400" />}
      <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 dark:text-slate-500">{label}</span>
      <span className="text-xs text-slate-700 dark:text-slate-200 font-semibold truncate">{value}</span>
    </div>
  );
}

function RelationshipsBlock({
  relationships,
  onRemove,
}: {
  relationships: PersonDetailResponse["relationships"];
  onRemove: (id: string) => void;
}) {
  if (relationships.length === 0) {
    return (
      <section>
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
          Relationships
        </h4>
        <p className="text-sm text-slate-400 dark:text-slate-500 italic">
          No relationship tags yet.
        </p>
      </section>
    );
  }
  return (
    <section>
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
        Relationships
      </h4>
      <ul className="flex flex-wrap gap-1.5">
        {relationships.map((r) => (
          <li
            key={r.id}
            className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 dark:bg-violet-950/40 text-violet-800 dark:text-violet-200 text-xs font-semibold px-2.5 py-1 border border-violet-200/60 dark:border-violet-800/50"
          >
            {humanizeRelationship(r.type, (r as { relationship_label_custom?: string | null }).relationship_label_custom)}
            <button
              type="button"
              onClick={() => onRemove(r.id)}
              className="opacity-60 hover:opacity-100"
              aria-label={`Remove ${r.type} relationship`}
            >
              <X className="w-3 h-3" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Two-tier contact-aggregation block.
 *
 *  Tier 1 (pre-confirm): shows the inferred field rollup with a
 *  single contact-level Confirm button. The standard happy path —
 *  user glances at the values, taps once.
 *
 *  Tier 2 (post-confirm): only renders fields with `pendingChangeCount`
 *  active. Other fields stay quiet (the user already confirmed the
 *  contact). Each pending field offers Confirm change / Reject /
 *  Keep both as variants. Once acted on, the field returns to solid
 *  and the block hides itself again. */
function FieldSourcesBlock({
  personId,
  onChanged,
}: {
  personId: string;
  onChanged: () => void;
}) {
  const [data, setData] = useState<PersonFieldsResponse | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmingContact, setConfirmingContact] = useState(false);

  const refresh = useCallback(() => {
    api.personFieldsGet(personId).then(setData).catch(() => undefined);
  }, [personId]);

  useEffect(refresh, [refresh]);

  if (!data || data.observations.length === 0) return null;

  const onConfirmContact = async () => {
    if (confirmingContact) return;
    setConfirmingContact(true);
    try {
      await api.personConfirmContact(personId);
      refresh();
      onChanged();
    } finally {
      setConfirmingContact(false);
    }
  };

  const onResolve = async (
    obsId: string,
    resolution: "replace" | "keep_both" | "reject",
  ) => {
    if (busyId) return;
    setBusyId(obsId);
    try {
      await api.personFieldResolveChange(obsId, resolution);
      refresh();
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  // Pre-confirm: render the digest of inferred values + a single
  // contact-level Confirm button.
  if (!data.isFullyConfirmed) {
    return (
      <section className="rounded-2xl border border-amber-300/70 dark:border-amber-700/40 bg-amber-50/40 dark:bg-amber-900/10 p-3 sm:p-4">
        <header className="flex items-center justify-between mb-2 gap-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-200 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            Inferred contact
          </h4>
          <span className="text-[11px] text-amber-700 dark:text-amber-300/80">
            Memso pulled these from your captures + connectors. Tap Confirm to lock them in.
          </span>
        </header>
        <ul className="space-y-1.5 mb-3">
          {(Object.keys(data.byField) as PeopleFieldName[]).map((field) => (
            <FieldDigestRow
              key={field}
              field={field}
              obs={data.byField[field] ?? []}
            />
          ))}
        </ul>
        <button
          type="button"
          onClick={onConfirmContact}
          disabled={confirmingContact}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm disabled:opacity-50"
        >
          <Check className="w-4 h-4" />
          {confirmingContact ? "Confirming..." : "Confirm contact"}
        </button>
      </section>
    );
  }

  // Post-confirm: render ONLY the fields with pending changes. If
  // nothing's pending, render nothing (the panel stays quiet and the
  // canonical fields above tell the whole story).
  if (data.pendingChangeCount === 0) return null;

  const pendingFields = data.pendingFields;
  return (
    <section>
      <header className="flex items-center gap-2 mb-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
          Field changes
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 normal-case">
            <Sparkles className="w-2.5 h-2.5" />
            {data.pendingChangeCount} new
          </span>
        </h4>
      </header>
      <ul className="space-y-2">
        {pendingFields.map((field) => (
          <PendingFieldRow
            key={field}
            field={field}
            obs={data.byField[field] ?? []}
            busyId={busyId}
            onResolve={onResolve}
          />
        ))}
      </ul>
    </section>
  );
}

/** Compact, tier-1 row: shows the field name + the highest-confidence
 *  proposed value alongside a small source pill. No per-row buttons —
 *  the user confirms everything in one tap via the parent's button. */
function FieldDigestRow({
  field,
  obs,
}: {
  field: PeopleFieldName;
  obs: FieldObservation[];
}) {
  const winner = obs[0]; // ORDER BY confirmed DESC, confidence DESC
  if (!winner) return null;
  return (
    <li className="flex items-baseline gap-2 text-sm">
      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 w-20 sm:w-24 flex-shrink-0">
        {FIELD_LABELS[field] ?? field}
      </span>
      <span className="text-slate-900 dark:text-slate-100 truncate flex-1">
        {field === "avatar_url" ? (
          <img
            src={winner.value}
            alt=""
            className="inline-block w-5 h-5 rounded-full object-cover bg-slate-200 dark:bg-slate-700 align-middle"
            referrerPolicy="no-referrer"
          />
        ) : (
          winner.value
        )}
      </span>
      <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider flex-shrink-0">
        {winner.source}
      </span>
    </li>
  );
}

/** Tier-2 row: a single field with a pending change. Shows the
 *  previous (confirmed) value + the new (pending) value side-by-side
 *  with three explicit actions. */
function PendingFieldRow({
  field,
  obs,
  busyId,
  onResolve,
}: {
  field: PeopleFieldName;
  obs: FieldObservation[];
  busyId: string | null;
  onResolve: (id: string, resolution: "replace" | "keep_both" | "reject") => void;
}) {
  const previous = obs.find((o) => o.confirmed && !o.field_change_pending);
  const pending = obs.find((o) => o.field_change_pending);
  if (!pending) return null;
  const multiValuedFields: ReadonlySet<PeopleFieldName> = new Set([
    "alt_email",
    "phone",
  ]);
  const isMultiValued = multiValuedFields.has(field);

  return (
    <li className="rounded-xl border border-amber-300/70 dark:border-amber-700/40 bg-amber-50/40 dark:bg-amber-900/10 overflow-hidden">
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-amber-800 dark:text-amber-200 bg-amber-100/60 dark:bg-amber-900/30 inline-flex items-center gap-1.5">
        <Sparkles className="w-2.5 h-2.5" />
        {FIELD_LABELS[field] ?? field} change
      </div>
      <div className="px-3 py-2.5 space-y-1.5">
        {previous && (
          <div className="flex items-baseline gap-2 text-sm">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 w-16 flex-shrink-0">
              Current
            </span>
            <span className="text-slate-700 dark:text-slate-200 truncate">
              {field === "avatar_url" ? (
                <img
                  src={previous.value}
                  alt=""
                  className="inline-block w-5 h-5 rounded-full object-cover bg-slate-200 dark:bg-slate-700 align-middle"
                  referrerPolicy="no-referrer"
                />
              ) : (
                previous.value
              )}
            </span>
            <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              {previous.source}
            </span>
          </div>
        )}
        <div className="flex items-baseline gap-2 text-sm">
          <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300 w-16 flex-shrink-0">
            New
          </span>
          <span className="text-slate-900 dark:text-slate-100 font-semibold truncate">
            {field === "avatar_url" ? (
              <img
                src={pending.value}
                alt=""
                className="inline-block w-5 h-5 rounded-full object-cover bg-slate-200 dark:bg-slate-700 align-middle"
                referrerPolicy="no-referrer"
              />
            ) : (
              pending.value
            )}
          </span>
          <span className="text-[10px] text-amber-700 dark:text-amber-300 uppercase tracking-wider">
            {pending.source} · {Math.round(pending.confidence * 100)}%
          </span>
        </div>
        <div className="flex items-center gap-1.5 pt-1 flex-wrap">
          <button
            type="button"
            onClick={() => onResolve(pending.id, "replace")}
            disabled={busyId === pending.id}
            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50"
          >
            <Check className="w-3 h-3" />
            Confirm change
          </button>
          {isMultiValued && (
            <button
              type="button"
              onClick={() => onResolve(pending.id, "keep_both")}
              disabled={busyId === pending.id}
              className="inline-flex items-center text-xs font-semibold text-slate-700 dark:text-slate-200 px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
            >
              Keep both
            </button>
          )}
          <button
            type="button"
            onClick={() => onResolve(pending.id, "reject")}
            disabled={busyId === pending.id}
            className="inline-flex items-center text-xs font-semibold text-slate-500 hover:text-rose-600 dark:hover:text-rose-300 px-2.5 py-1.5 rounded-md hover:bg-rose-50 dark:hover:bg-rose-950/30 disabled:opacity-50"
          >
            Reject
          </button>
        </div>
      </div>
    </li>
  );
}

const FIELD_LABELS: Partial<Record<PeopleFieldName, string>> = {
  display_name: "Name",
  primary_email: "Primary email",
  alt_email: "Alternate email",
  phone: "Phone",
  role: "Role",
  organization: "Organization",
  avatar_url: "Photo",
  notes: "Notes",
};

/** Inline "Important dates" for the Person card. Lists the
 *  birthdays, anniversaries, and events Memso has woven for this
 *  contact. Inferred rows render dotted with a one-tap Confirm per
 *  the inferred-vs-confirmed pattern. Hides itself when there are no
 *  dates so the detail panel stays quiet. */
function ImportantDatesBlock({ personId }: { personId: string }) {
  const [dates, setDates] = useState<DateItem[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [gone, setGone] = useState<Set<string>>(new Set());

  const refresh = useCallback(() => {
    api
      .datesForPerson(personId)
      .then((d) => setDates(d.dates))
      .catch(() => setDates([]));
  }, [personId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const confirm = async (id: string) => {
    if (busy) return;
    setBusy(id);
    try {
      await api.dateConfirm(id);
      refresh();
    } finally {
      setBusy(null);
    }
  };

  const reject = async (id: string) => {
    if (busy) return;
    setBusy(id);
    try {
      await api.dateReject(id);
      setGone((prev) => new Set(prev).add(id));
    } finally {
      setBusy(null);
    }
  };

  const visible = (dates ?? []).filter((d) => !gone.has(d.date_id));
  if (visible.length === 0) return null;

  return (
    <section>
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
        Important dates
      </h4>
      <ul className="space-y-1.5">
        {visible.map((d) => {
          const dotted = d.is_inferred && !d.confirmed;
          const since = originYearLabel(d);
          return (
            <li
              key={d.date_id}
              className={`rounded-xl px-3 py-2 flex items-start gap-2.5 ${
                dotted
                  ? "border border-dashed border-amber-400/70 dark:border-amber-500/60 bg-amber-50/50 dark:bg-amber-950/20"
                  : "bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700"
              }`}
            >
              <span className="text-[10px] uppercase tracking-wider font-bold mt-0.5 px-1.5 py-0.5 rounded-full bg-pink-200 dark:bg-pink-700/40 text-pink-800 dark:text-pink-200 flex-shrink-0">
                {dateKindLabel(d.kind)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {formatNextOccurrence(d.next_occurrence)}
                  </span>
                  <span className="text-[11px] font-semibold text-rose-600 dark:text-rose-300">
                    {relativeDayChip(d.days_until)}
                  </span>
                  {since && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                      {since}
                    </span>
                  )}
                </div>
                {d.description && (
                  <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 line-clamp-2">
                    {d.description}
                  </div>
                )}
                {dotted && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <button
                      type="button"
                      onClick={() => confirm(d.date_id)}
                      disabled={busy === d.date_id}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold px-1.5 py-0.5 disabled:opacity-50"
                    >
                      <Check className="w-3 h-3" />
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={() => reject(d.date_id)}
                      disabled={busy === d.date_id}
                      className="inline-flex items-center text-[10px] font-semibold text-slate-500 hover:text-rose-600 dark:hover:text-rose-300 px-1 py-0.5 disabled:opacity-50"
                      aria-label="Dismiss"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function NodeNoteBlock({ personId }: { personId: string }) {
  const [note, setNote] = useState<NodeNote | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .nodeNoteGet(personId)
      .then((r) => setNote(r.note))
      .catch(() => setNote(null))
      .finally(() => setLoading(false));
  }, [personId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <section>
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
          Memso wiki note
        </h4>
        <p className="text-sm text-slate-400 dark:text-slate-500 italic">Loading…</p>
      </section>
    );
  }
  if (!note) {
    return (
      <section>
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
          Memso wiki note
        </h4>
        <p className="text-sm text-slate-400 dark:text-slate-500 italic">
          Memso has not synthesized a note for this contact yet. It will appear here once
          the wiki synthesizer reaches this node.
        </p>
      </section>
    );
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Memso wiki note
        </h4>
        <div className="flex items-center gap-2">
          {note.obsidian_uri && (
            <a
              href={note.obsidian_uri}
              className="px-2 py-1 rounded-lg text-[11px] font-semibold border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              Edit in Obsidian
            </a>
          )}
          <button
            type="button"
            disabled={refreshing}
            onClick={async () => {
              setRefreshing(true);
              try {
                await api.nodeNoteRefresh(personId);
                load();
              } catch {
                /* rate-limited or offline; ignore */
              } finally {
                setRefreshing(false);
              }
            }}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>
      {note.conflict_state === "user_frozen" && (
        <p className="mb-2 text-[11px] text-amber-600 dark:text-amber-400">
          You edited this note in Obsidian, so Memso froze auto-generation to keep your
          changes. Re-synthesize from Memso Noticed if you want it regenerated.
        </p>
      )}
      <div className="rounded-xl px-3 py-2.5 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 max-h-[28rem] overflow-y-auto">
        <WikiNote markdown={note.body_markdown} />
      </div>
      {note.last_synthesized_at && (
        <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
          Synthesized {new Date(note.last_synthesized_at).toLocaleString()}
          {note.vault_synced ? " · synced to Obsidian" : ""}
        </p>
      )}
    </section>
  );
}

function InteractionsBlock({ interactions }: { interactions: PersonDetailResponse["interactions"] }) {
  if (interactions.length === 0) {
    return (
      <section>
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
          Recent activity
        </h4>
        <p className="text-sm text-slate-400 dark:text-slate-500 italic">
          No interactions logged yet.
        </p>
      </section>
    );
  }
  return (
    <section>
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
        Recent activity
      </h4>
      <ol className="space-y-1.5">
        {interactions.map((i) => (
          <li
            key={i.id}
            className="flex items-start gap-2.5 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700"
          >
            <span className="text-[10px] uppercase tracking-wider font-bold mt-0.5 px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 flex-shrink-0">
              {kindLabel(i.kind)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-800 dark:text-slate-100 truncate">
                {i.summary || "(no summary)"}
              </div>
              <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                {relativeTime(i.occurred_at)}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function WeaveInsightsBlock({ insights }: { insights: NonNullable<PersonDetailResponse["weave_insights"]> }) {
  if (!insights || insights.length === 0) return null;
  // Surface the freshest, highest-confidence cross-source inferences
  // that the Continuous Weave synthesizer produced about this person.
  return (
    <section>
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
        Memso woven inferences
      </h4>
      <ol className="space-y-1.5">
        {insights.map((i) => {
          const conf = typeof i.confidence === "number" ? Math.round(i.confidence * 100) : null;
          const isInferred = conf !== null && conf < 85;
          return (
            <li
              key={i.id}
              className={`rounded-xl px-3 py-2 text-sm border ${
                isInferred
                  ? "bg-violet-50 dark:bg-violet-950/20 border-dashed border-violet-300/70 dark:border-violet-700/40 text-slate-800 dark:text-slate-100"
                  : "bg-violet-50 dark:bg-violet-950/20 border-violet-200/60 dark:border-violet-800/40 text-slate-800 dark:text-slate-100"
              }`}
            >
              <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700 dark:text-violet-300 mb-1 flex items-center gap-2">
                <span>{i.kind.replace(/_/g, " ")}</span>
                {conf !== null && <span className="opacity-70">· {conf}% confidence</span>}
                {isInferred && <span className="opacity-70">· inferred</span>}
              </div>
              <div className="font-medium">{i.title}</div>
              {i.body && (
                <div className="mt-1 whitespace-pre-wrap text-slate-700 dark:text-slate-300 line-clamp-3">
                  {i.body}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function NoteMentionsBlock({ mentions }: { mentions: PersonDetailResponse["noteMentions"] }) {
  if (mentions.length === 0) return null;
  return (
    <section>
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
        Mentioned in notes
      </h4>
      <ol className="space-y-1.5">
        {mentions.map((m) => (
          <li
            key={m.id}
            className="rounded-xl px-3 py-2 bg-amber-50 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-800/40 text-sm text-slate-800 dark:text-slate-100"
          >
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 dark:text-amber-300 mb-1">
              {m.type} · {relativeTime(m.captured_at)}
            </div>
            <div className="whitespace-pre-wrap line-clamp-3">{m.text}</div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function EditPersonForm({
  person,
  onCancel,
  onSaved,
}: {
  person: Person;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState(person.display_name);
  const [primaryEmail, setPrimaryEmail] = useState(person.primary_email ?? "");
  const [role, setRole] = useState(person.role ?? "");
  const [organization, setOrganization] = useState(person.organization ?? "");
  const [notes, setNotes] = useState(person.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      className="px-4 sm:px-6 py-5 space-y-4"
      onSubmit={async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
          await api.personUpdate(person.id, {
            display_name: displayName.trim(),
            primary_email: primaryEmail.trim() || null,
            role: role.trim() || null,
            organization: organization.trim() || null,
            notes: notes.trim() || null,
          });
          onSaved();
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
        } finally {
          setSaving(false);
        }
      }}
    >
      <Field label="Name" required>
        <input
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Email">
        <input
          type="email"
          value={primaryEmail}
          onChange={(e) => setPrimaryEmail(e.target.value)}
          className={inputCls}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Role">
          <input value={role} onChange={(e) => setRole(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Organization">
          <input
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
            className={inputCls}
          />
        </Field>
      </div>
      <Field label="Notes">
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={`${inputCls} resize-none`}
        />
      </Field>
      {error && <div className="text-xs text-rose-600 dark:text-rose-300">{error}</div>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving || !displayName.trim()}
          className="flex-1 px-3 py-2 rounded-xl text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-2 rounded-xl text-sm font-semibold border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function AddPersonDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [role, setRole] = useState("");
  const [organization, setOrganization] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/40 dark:bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">Add person</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <form
          className="px-5 py-4 space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setSaving(true);
            setError(null);
            try {
              const r = await api.personCreate({
                display_name: displayName.trim(),
                primary_email: primaryEmail.trim() || null,
                role: role.trim() || null,
                organization: organization.trim() || null,
                notes: notes.trim() || null,
              });
              onCreated(r.person.id);
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err));
            } finally {
              setSaving(false);
            }
          }}
        >
          <Field label="Name" required>
            <input
              required
              autoFocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Alice Example"
              className={inputCls}
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={primaryEmail}
              onChange={(e) => setPrimaryEmail(e.target.value)}
              placeholder="alice@example.com"
              className={inputCls}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <input
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="PM"
                className={inputCls}
              />
            </Field>
            <Field label="Organization">
              <input
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                placeholder="Memso"
                className={inputCls}
              />
            </Field>
          </div>
          <Field label="Notes">
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`${inputCls} resize-none`}
            />
          </Field>
          {error && <div className="text-xs text-rose-600 dark:text-rose-300">{error}</div>}
          <button
            type="submit"
            disabled={saving || !displayName.trim()}
            className="w-full px-3 py-2 rounded-xl text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {saving ? "Adding…" : "Add to directory"}
          </button>
        </form>
      </div>
    </div>
  );
}

function AddRelationshipDialog({
  personId,
  personName,
  onClose,
  onCreated,
}: {
  personId: string;
  personName: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [type, setType] = useState<RelationshipType>("colleague");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const types = useMemo(() => Object.keys(LEGACY_RELATIONSHIP_LABELS) as RelationshipType[], []);
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/40 dark:bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">
            Tag {personName} as
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <form
          className="px-5 py-4 space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setSaving(true);
            setError(null);
            try {
              await api.relationshipCreate({
                person_id: personId,
                type,
                notes: notes.trim() || undefined,
              });
              onCreated();
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err));
            } finally {
              setSaving(false);
            }
          }}
        >
          <div className="grid grid-cols-2 gap-1.5">
            {types.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                aria-pressed={type === t}
                className={`text-xs font-semibold py-2 rounded-lg border transition-colors ${
                  type === t
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-white/60 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-violet-300"
                }`}
              >
                {LEGACY_RELATIONSHIP_LABELS[t]}
              </button>
            ))}
          </div>
          <Field label="Notes (optional)">
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`${inputCls} resize-none`}
            />
          </Field>
          {error && <div className="text-xs text-rose-600 dark:text-rose-300">{error}</div>}
          <button
            type="submit"
            disabled={saving}
            className="w-full px-3 py-2 rounded-xl text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {saving ? "Tagging…" : `Tag as ${LEGACY_RELATIONSHIP_LABELS[type] ?? type}`}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}

const inputCls =
  "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400";

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "·";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

function relativeTime(iso: string | null): string {
  if (!iso) return "·";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "·";
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  const y = Math.floor(d / 365);
  return `${y}y ago`;
}

function kindLabel(kind: string): string {
  switch (kind) {
    case "email":
      return "Email";
    case "calendar-event":
      return "Meeting";
    case "voice-mention":
      return "Voice";
    case "note-mention":
      return "Note";
    default:
      return kind;
  }
}

// Building2 + Mail are imported for icon usage; keep refs alive in case
// the linter strips unused imports during a future refactor.
void Building2;
